import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  workOrdersApi,
  QueryWorkOrderParams,
  CreateWorkOrderDto,
  UpdateWorkOrderDto,
  WorkOrderUpdateStatusDto,
  CompleteWorkOrderDto,
} from '../services/work-orders.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated work orders list
 */
export function useWorkOrdersList(params: QueryWorkOrderParams = {}) {
  return useQuery({
    queryKey: queryKeys.workOrders.list(params),
    queryFn: () => workOrdersApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single work order
 */
export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.detail(id || ''),
    queryFn: () => workOrdersApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching work orders dashboard data
 */
export function useWorkOrdersDashboard() {
  return useQuery({
    queryKey: [...queryKeys.workOrders.all, 'dashboard'],
    queryFn: () => workOrdersApi.getDashboard(),
  });
}

/**
 * Mutation hook for creating a work order
 */
export function useCreateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkOrderDto) => workOrdersApi.create(data),
    onSuccess: () => {
      toast.success('Work order created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a work order
 */
export function useUpdateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkOrderDto }) =>
      workOrdersApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Work order updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating work order status
 */
export function useUpdateWorkOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkOrderUpdateStatusDto }) =>
      workOrdersApi.updateStatus(id, data),
    onSuccess: (_, variables) => {
      toast.success('Status updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.id) });
      // Also invalidate equipment if status change affects it
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for completing a work order
 */
export function useCompleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompleteWorkOrderDto }) =>
      workOrdersApi.complete(id, data),
    onSuccess: (_, variables) => {
      toast.success('Work order completed successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.id) });
      // Invalidate related modules that may be affected
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a work order
 */
export function useDeleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workOrdersApi.delete(id),
    onSuccess: () => {
      toast.success('Work order deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for adding a line item to a work order
 */
export function useAddLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      data,
    }: {
      workOrderId: string;
      data: { itemId: string; plannedQty: number; unit: string; unitCost?: number; notes?: string };
    }) => workOrdersApi.addLineItem(workOrderId, data),
    onSuccess: (_, variables) => {
      toast.success('Line item added successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for removing a line item from a work order
 */
export function useRemoveLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, itemId }: { workOrderId: string; itemId: string }) =>
      workOrdersApi.removeLineItem(workOrderId, itemId),
    onSuccess: (_, variables) => {
      toast.success('Line item removed successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for issuing parts from a work order line item
 */
export function useIssueParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
      quantity,
    }: {
      workOrderId: string;
      itemId: string;
      quantity: number;
    }) => workOrdersApi.issueParts(workOrderId, itemId, quantity),
    onSuccess: (_, variables) => {
      toast.success('Parts issued successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for returning parts from a work order line item
 */
export function useReturnParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
      quantity,
      notes,
    }: {
      workOrderId: string;
      itemId: string;
      quantity: number;
      notes?: string;
    }) => workOrdersApi.returnParts(workOrderId, itemId, quantity, notes),
    onSuccess: (_, variables) => {
      toast.success('Parts returned successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
